import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Box,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useDebouncedCallback } from 'use-debounce';

import CircularSlider from '@fseehawer/react-circular-slider';

import { GroundStationManager, AntennaManager } from 'models';
import { round, equalWithTolerance } from 'utils';
import { useAppDispatch, useAppSelector } from 'hooks';
import { sendGroundStationCommand } from 'state/groundstations';
import { receiveMessage } from 'state/websockets';

import RefreshButton from 'components/RefreshButton';
import AzimuthSwatch from 'components/AzimuthSwatch';
import ElevationSwatch from 'components/ElevationSwatch';

const DEBOUNCE_TIME = 500; // millis

interface Props {
    antenna: AntennaManager.Antenna;
}

type Position = { elevation: number; azimuth: number };

const UpdatePosition: React.FunctionComponent<Props> = ({ antenna }) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const { groundstation } = antenna;

    const { latestCommands } = useAppSelector((root) => root.groundstations);

    const currentPosition: Position = {
        azimuth: antenna.lastKnownAzimuth,
        elevation: antenna.lastKnownElevation,
    };

    const [lockControls, setLockControls] = useState(true);

    const [desiredPosition, setDesiredPosition] = useState<Position>({
        azimuth: antenna.lastKnownAzimuth,
        elevation: antenna.lastKnownElevation,
    });

    const [isDragging, setIsDragging] = useState(false);

    const [sentCommand, setSentCommand] =
        useState<GroundStationManager.API.Command>();

    const [sending, setSending] = useState(false);

    const getPosition = useCallback(async () => {
        if (!groundstation.connectionId) return;

        setSending(true);

        try {
            const results = await dispatch(
                sendGroundStationCommand({
                    id: groundstation.id,
                    command: 'get_position',
                    params: {},
                }),
            );

            if (results.error)
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            results.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );

            if (results.command) setSentCommand(results.command);
        } finally {
            setSending(false);
        }
    }, [antenna.groundstation]);

    const setPosition = useCallback(
        async ({ azimuth, elevation }: Position) => {
            if (!groundstation.connectionId) return;

            setSending(true);

            try {
                const results = await dispatch(
                    sendGroundStationCommand({
                        id: groundstation.id,
                        command: 'set_position',
                        params: {
                            azimuth,
                            elevation,
                        },
                    }),
                );

                if (results.error)
                    dispatch(
                        receiveMessage({
                            command: 'message',
                            is_ground_station: true,
                            from_id: groundstation.id,
                            from: groundstation.name,
                            message:
                                results.error ??
                                'No Error Message from Ground Station',
                            status: 'error',
                        }),
                    );

                if (results.command) setSentCommand(results.command);
            } finally {
                setSending(false);
            }
        },
        [antenna.groundstation],
    );

    const setDesiredPositionDebounced = useDebouncedCallback(
        setDesiredPosition,
        DEBOUNCE_TIME,
    );

    useEffect(() => {
        const latestSentCommand =
            sentCommand != null ? latestCommands[sentCommand.id] : null;

        if (latestSentCommand && groundstation) {
            if (latestSentCommand.status === 'FINISHED') {
                setSentCommand(undefined);
            } else if (latestSentCommand.status === 'ERROR') {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            latestSentCommand.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );
            }
        }
    }, [latestCommands]);

    useEffect(() => {
        if (
            groundstation.connectionId &&
            !lockControls &&
            !isDragging &&
            !(
                equalWithTolerance(
                    desiredPosition.azimuth,
                    currentPosition.azimuth,
                    0.25,
                ) &&
                equalWithTolerance(
                    desiredPosition.elevation,
                    currentPosition.elevation,
                    0.25,
                )
            )
        ) {
            setPosition(desiredPosition);
        }
    }, [desiredPosition, isDragging]);

    return (
        <Paper
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: 2,
                    [theme.breakpoints.down('sm')]: {
                        flexDirection: 'column',
                    },
                    [theme.breakpoints.up('sm')]: {
                        flexDirection: 'row',
                    },
                }}
            >
                <Box>
                    <Typography variant="h6">
                        Azimuth & Elevation Control
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                    }}
                >
                    <RefreshButton
                        isRefreshing={sending}
                        onClick={() => getPosition()}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 1,
                    [theme.breakpoints.down('sm')]: {
                        flexDirection: 'column',
                    },
                    [theme.breakpoints.up('sm')]: {
                        flexDirection: 'row',
                    },
                }}
            >
                <Box>
                    <ToggleButtonGroup
                        exclusive
                        value={lockControls}
                        onChange={(_, checked: boolean) => {
                            if (checked == null) return;

                            setLockControls(checked);
                        }}
                    >
                        <ToggleButton value={false}>Unlock</ToggleButton>
                        <ToggleButton value={true}>Lock</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box>
                    <Button
                        sx={{ height: '100%' }}
                        variant="contained"
                        disabled={lockControls || !groundstation.connectionId}
                        onClick={() => {
                            setDesiredPosition({
                                azimuth: 0,
                                elevation: 0,
                            });
                        }}
                    >
                        Set to 0,0
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gap: 2,
                    mt: 1,
                    [theme.breakpoints.down('sm')]: {
                        gridTemplateColumns: '1fr',
                    },
                    [theme.breakpoints.up('sm')]: {
                        gridTemplateColumns: '1fr 1fr',
                    },
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ pb: 1 }}>
                        Current Azimuth
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                        }}
                    >
                        <AzimuthSwatch
                            rotation={currentPosition.azimuth}
                            size={36}
                            margin={2}
                            padding={1}
                        />
                        <Typography variant="h5" align="center">
                            {round(currentPosition.azimuth, 2)}°
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ pb: 1 }}>
                        Current Elevation
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                        }}
                    >
                        <ElevationSwatch
                            elevation={currentPosition.elevation}
                            size={36}
                            margin={2}
                            padding={1}
                        />
                        <Typography variant="h5" align="center">
                            {round(currentPosition.elevation, 2)}°
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ pb: 2 }}>
                        Desired Azimuth
                    </Typography>
                    <CircularSlider
                        label="Azimuth"
                        initialValue={round(currentPosition.azimuth, 1)}
                        dataIndex={round(desiredPosition.azimuth, 1)}
                        min={-360}
                        max={360}
                        direction={1}
                        knobPosition="top"
                        appendToValue="°"
                        labelFontSize="1rem"
                        valueFontSize="1.5rem"
                        verticalOffset="1rem"
                        width={128}
                        knobDraggable={!lockControls}
                        trackDraggable={!lockControls}
                        onChange={(value: number) =>
                            setDesiredPositionDebounced((old) => ({
                                ...old,
                                azimuth: round(value, 2),
                            }))
                        }
                        isDragging={(value: boolean) => {
                            setIsDragging(value);
                        }}
                        continuous={{
                            enabled: true,
                            clicks: 360,
                            interval: 1,
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ pb: 2 }}>
                        Desired Elevation
                    </Typography>
                    <CircularSlider
                        label="Elevation"
                        initialValue={round(currentPosition.elevation, 1)}
                        dataIndex={round(desiredPosition.elevation, 1)}
                        min={0}
                        max={90}
                        direction={-1}
                        knobPosition="right"
                        appendToValue="°"
                        labelFontSize="1rem"
                        valueFontSize="1.5rem"
                        verticalOffset="1rem"
                        width={128}
                        knobDraggable={!lockControls}
                        trackDraggable={!lockControls}
                        onChange={(value: number) =>
                            setDesiredPositionDebounced((old) => ({
                                ...old,
                                elevation: value,
                            }))
                        }
                        isDragging={(value: boolean) => {
                            setIsDragging(value);
                        }}
                        continuous={{
                            enabled: true,
                            clicks: 360,
                            interval: 1,
                        }}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

UpdatePosition.displayName = 'UpdatePosition';
export default UpdatePosition;
